<template>
  <div class="sign-up-page">
    <section class="signup-card">

      <div class="signup-illustration">
        <img
          src="@/assets/images/set-password.svg"
          alt="Set password"
        >
      </div>

      <h1>Set a new password</h1>

      <p class="signup-description">
        You've been invited to join DTX Manufacturing.<br>
        Set a strong password to activate your account.
      </p>

      <div class="signup-form">

        <div
          class="form-field"
          data-validation="Password"
        >
          <label>
            New password <span>*</span>
          </label>

          <div class="input-view">
            <span class="lock-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="5"
                  y="10"
                  width="14"
                  height="10"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M8 10V7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7V10"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </span>

            <input
              v-model="password"
              name="password"
              autocomplete="off"
              placeholder="Enter new password"
              :type="passwordType"
            >

            <img
              v-if="passwordType === 'password'"
              src="@/assets/images/icons/open-eye.svg"
              alt="Show password"
              @click="passwordType = 'text'"
            >

            <img
              v-else
              src="@/assets/images/icons/hide-eye.svg"
              alt="Hide password"
              @click="passwordType = 'password'"
            >
          </div>
        </div>

        <div class="password-requirements">
          <div class="requirements-title">Password must contain:</div>

          <div class="requirements-grid">
            <div class="requirement" :class="{ valid: hasMinLength }">
              <span class="requirement-icon">✓</span>
              <span>At least 8 characters</span>
            </div>

            <div class="requirement" :class="{ valid: hasNumber }">
              <span class="requirement-icon">✓</span>
              <span>One number</span>
            </div>

            <div class="requirement" :class="{ valid: hasUppercase }">
              <span class="requirement-icon">✓</span>
              <span>One uppercase letter</span>
            </div>

            <div class="requirement" :class="{ valid: hasSpecialCharacter }">
              <span class="requirement-icon">✓</span>
              <span>One special character</span>
            </div>

            <div class="requirement" :class="{ valid: hasLowercase }">
              <span class="requirement-icon">✓</span>
              <span>One lowercase letter</span>
            </div>
          </div>
        </div>

        <div
          class="form-field confirm-password-field"
          data-validation="ConfirmPassword"
        >
          <label>
            Confirm new password <span>*</span>
          </label>

          <div class="input-view">
            <span class="lock-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="5"
                  y="10"
                  width="14"
                  height="10"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
                <path
                  d="M8 10V7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7V10"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </span>

            <input
              v-model="confirmPassword"
              autocomplete="off"
              name="confirmPassword"
              placeholder="Confirm new password"
              :type="confirmPasswordType"
            >

            <img
              v-if="confirmPasswordType === 'password'"
              src="@/assets/images/icons/open-eye.svg"
              alt="Show password"
              @click="confirmPasswordType = 'text'"
            >

            <img
              v-else
              src="@/assets/images/icons/hide-eye.svg"
              alt="Hide password"
              @click="confirmPasswordType = 'password'"
            >
          </div>
          <span
            v-if="confirmPassword && !passwordsMatch"
            class="password-error"
          >
            Passwords do not match.
          </span>
        </div>

        <div
          v-if="showTermsCheckbox"
          class="terms-section"
        >
          <div class="terms-checkbox">

            <input
              id="agreeTerms"
              v-model="termsAccepted"
              type="checkbox"
            >

            <label for="agreeTerms">
              Accept Terms and Conditions
            </label>

            <div
              class="info-icon"
              @click="openTermsModal"
            >
              <img
                src="@/assets/images/icons/info-icon.svg"
                width="18"
                alt="Terms information"
              >
            </div>

          </div>
        </div>

        <button
          type="button"
          class="submit-btn"
          :disabled="isSubmitDisabled"
          @click="trySignup"
        >
          Set Password
        </button>

      </div>

      <div class="divider">
        <span></span>
        <label>OR</label>
        <span></span>
      </div>

      <button
        type="button"
        class="back-login"
        @click="$router.push('/login')"
      >
        <span class="arrow">←</span>
        Back to login
      </button>

    </section>

    <modal
      :open="showTermsModal"
      @close="showTermsModal = false"
      title="Terms and Conditions"
      :scroll="false"
    >
      <div class="terms-viewer-container">

        <div
          v-if="loadingTerms"
          class="terms-loader"
        >
          <span class="loader-small"></span>
          <p>Loading Terms...</p>
        </div>

        <iframe
          v-else-if="termsUrl"
          :src="termsUrl"
          frameborder="0"
        />

        <p
          v-else
          class="error-message"
        >
          Failed to load terms and conditions
        </p>

      </div>
    </modal>
  </div>
</template>

<script>
import signupValidation from '@/helper/formValidation/signupValidation.js'
import { SignupUser } from '@/api/session.js'
import { alert, success } from '@/plugins/notification'
import { getTermsHistory, generateS3DownloadingUrl } from '@/api'
import modal from '@/components/common/modal.vue'

export default {
  name: 'SignupPage',
  components: { modal },
  data () {
    return {
      password: '',
      confirmPassword: '',
      passwordType: 'password',
      confirmPasswordType: 'password',
      termsAccepted: false,
      showTermsModal: false,
      termsUrl: '',
      loadingTerms: false,
      termId: null
    }
  },
  computed: {
    signupToken () {
      return this.$route.query.token || ''
    },
    showTermsCheckbox () {
      return this.$route.query.agreeTerms === 'true'
    },
    hasMinLength () {
      return this.password.length >= 8
    },
    hasNumber () {
      return /\d/.test(this.password)
    },
    hasUppercase () {
      return /[A-Z]/.test(this.password)
    },
    hasLowercase () {
      return /[a-z]/.test(this.password)
    },
    hasSpecialCharacter () {
      return /[^A-Za-z0-9]/.test(this.password)
    },
    isPasswordValid () {
      return (
        this.hasMinLength &&
      this.hasNumber &&
      this.hasUppercase &&
      this.hasLowercase &&
      this.hasSpecialCharacter
      )
    },
    passwordsMatch () {
      return this.password === this.confirmPassword
    },
    isSubmitDisabled () {
      return (
        !this.isPasswordValid ||
      !this.confirmPassword ||
      !this.passwordsMatch ||
      (this.showTermsCheckbox && !this.termsAccepted)
      )
    }
  },
  watch: {
    signupToken: {
      immediate: true,
      handler (val) {
        if (val) {
          this.fetchLatestTerms()
        }
      }
    }
  },
  methods: {
    async fetchLatestTerms () {
      if (!this.signupToken) return
      try {
        const response = await getTermsHistory(this.signupToken)
        const termsConditions = response.core_terms_condition
        if (termsConditions?.length) {
          const latestTerms = termsConditions[0]
          this.termId = latestTerms.id
        }
      } catch (err) {
        console.error('Failed to fetch terms ID:', err)
      }
    },
    trySignup () {
      if (this.showTermsCheckbox && !this.termsAccepted) {
        alert('Please accept the Terms and Conditions')
        return
      }
      const body = {
        password: this.password,
        confirmPassword: this.confirmPassword
      }
      if (signupValidation(body)) {
        delete body.confirmPassword
        SignupUser(this.password, this.signupToken, this.termId).then(data => {
          if (data.message === 'Signup Successful') {
            success('You signed up successfully.')
            this.$router.push('/login')
          } else {
            alert('There was an error signing up.')
          }
        }).catch(() => {
          alert('There was an error signing up.')
        })
      }
    },
    async openTermsModal () {
      this.showTermsModal = true
      this.loadingTerms = true
      try {
        const response = await getTermsHistory(this.signupToken)
        const termsConditions = response.core_terms_condition
        if (termsConditions?.length) {
          const latestTerms = termsConditions[0]
          const { url } = await generateS3DownloadingUrl({
            S3Objects: [{
              fileName: 'terms',
              S3Key: latestTerms.blob_key
            }]
          })
          if (url?.length) {
            const response = await fetch(url[0].url)
            const blob = await response.blob()
            this.termsUrl = URL.createObjectURL(blob.slice(0, blob.size, 'application/pdf'))
          }
        }
      } catch (err) {
        console.error('Failed to load terms:', err)
        alert('Failed to load terms and conditions')
      } finally {
        this.loadingTerms = false
      }
    },

    keyPress (e) {
      if (e instanceof KeyboardEvent && e.code === 'Enter') {
        this.trySignup()
      }
    }
  },
  created () {
    document.body.addEventListener('keydown', this.keyPress)
  },
  beforeDestroy () {
    document.body.removeEventListener('keydown', this.keyPress)
    if (this.termsUrl) {
      URL.revokeObjectURL(this.termsUrl)
    }
  }
}
</script>

<style lang="scss" scoped>
.sign-up-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  box-sizing: border-box;
  background: #faf8f4;
  font-family: "Nunito Sans", sans-serif;
}

.signup-card {
  width: 100%;
  max-width: 490px;
  box-sizing: border-box;
  padding: 18px 42px 24px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07);
  text-align: center;
}

.signup-illustration {
  width: 100%;
  height: 170px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signup-illustration img {
  width: 245px;
  height: 170px;
  object-fit: contain;
}

.signup-card h1 {
  margin: 0;
  color: #34383d;
  font-size: 27px;
  line-height: 36px;
  font-weight: 600;
}

.signup-description {
  margin: 8px 0 26px;
  color: #6f7378;
  font-size: 14px;
  line-height: 22px;
}

.signup-form {
  width: 100%;
  text-align: left;
}

.form-field {
  width: 100%;
  margin-bottom: 18px;
}

.form-field label {
  display: block;
  margin-bottom: 7px;
  color: #45484c;
  font-size: 13px;
  font-weight: 500;
}

.form-field label span {
  color: #c7983a;
}

.input-view {
  position: relative;
  width: 100%;
  height: 37px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid #dedede;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-view:focus-within {
  border-color: #c79a3c;

  box-shadow: 0 0 0 2px rgba(199, 154, 60, 0.08);
}

.lock-icon {
  width: 39px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #8b8f93;
}

.lock-icon svg {
  width: 14px;
  height: 14px;
}

.input-view input {
  min-width: 0;
  flex: 1;
  height: 100%;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #444;
  font-size: 13px;
}

.input-view input::placeholder {
  color: #9b9da0;
}

.input-view > img {
  width: 14px;
  height: 14px;
  margin-right: 12px;
  padding: 0;
  cursor: pointer;
  opacity: 0.7;
}

.password-requirements {
  margin: 5px 0 18px;
}

.requirements-title {
  margin-bottom: 7px;
  color: #555b60;
  font-size: 12px;
  font-weight: 500;
}

.requirements-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 18px;
  row-gap: 6px;
}

.requirement {
  display: flex;
  align-items: center;
  color: #d9534f;
  font-size: 12px;
  line-height: 16px;
}

.requirement-icon {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 6px;
  box-sizing: border-box;
  border: 1px solid #d9534f;
  border-radius: 50%;
  color: #d9534f;
  font-size: 8px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.requirement.valid {
  color: #555b60;
}

.requirement.valid .requirement-icon {
  background: #c59638;
  border-color: #c59638;
  color: #fff;
}

.password-error {
  display: block;
  margin-top: 6px;
  color: #d9534f;
  font-size: 12px;
  line-height: 16px;
}

.divider {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
  margin: 20px auto 14px;
}

.divider span {
  flex: 1;
  height: 1px;
  background: #f1efed;
}

.divider label {
  margin: 0 15px;
  color: #858585;
  font-size: 12px;
  font-weight: 500;
}

.confirm-password-field {
  margin-bottom: 18px;
}

.terms-section {
  width: 100%;
  margin-bottom: 18px;
}

.terms-checkbox {
  display: flex;
  align-items: center;
  gap: 7px;
}

.terms-checkbox input[type='checkbox'] {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #c59638;
  cursor: pointer;
}

.terms-checkbox label {
  margin: 0;
  color: #555b60;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}

.info-icon {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.info-icon img {
  width: 16px;
}

.submit-btn {
  width: 100%;
  height: 35px;
  margin-top: 1px;
  border: 0;
  border-radius: 4px;
  background: #c59638;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  background: #b78931;
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-login {
  border: 0;
  background: transparent;
  color: #c18f2e;
  font-size: 13px;
  cursor: pointer;
}

.back-login .arrow {
  margin-right: 2px;
  font-size: 17px;
  vertical-align: -1px;
  display: inline-block;
  transform: scaleX(0.7);
  transform-origin: center;
}

.back-login:hover {
  text-decoration: underline;
}

.terms-viewer-container {
  width: 85vw;
  max-width: 95%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.terms-viewer-container iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.terms-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.terms-loader p {
  color: #666;
  font-size: 0.875rem;
}

.loader-small {
  width: 16px;
  height: 16px;
  display: inline-block;
  border: 2px solid #ccc;
  border-top: 2px solid #c59638;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.error-message {
  color: #666;
  font-size: var(--text-base);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 600px) {
  .sign-up-page {
    padding: 20px 15px;
  }

  .signup-card {
    padding: 15px 25px 22px;
  }

  .signup-illustration {
    height: 145px;
  }

  .signup-illustration img {
    width: 215px;
    height: 145px;
  }

  .signup-card h1 {
    font-size: 24px;
  }

  .signup-description {
    font-size: 13px;
  }
}

@media (max-height: 700px) {
  .sign-up-page {
    align-items: flex-start;
    padding-top: 15px;
    padding-bottom: 15px;
    overflow-y: auto;
  }

  .signup-card {
    padding-top: 15px;
  }

  .signup-illustration {
    height: 110px;
  }

  .signup-illustration img {
    height: 110px;
  }
}
</style>
